'use client'
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import useSWR, { mutate } from "swr";
import Image from 'next/image';

import Table from '@/components/organisms/Table/Table';
import TrendDescription from '@/app/[locale]/trends/TrendDescription';
import SearchDropdown from '@/components/atoms/SearchDropdown/SearchDropdown';
import StatTypeFilter from '@/components/molecules/Filters/StatTypeFilter';
import { Header, StatType, TableKeys } from '@/types/types';

import mockedTrends from "../../../data/mocks/trendsMock.json"
import "./_trends.scss";
import useWindowSize from '@/hooks/useWindowSize';

const fetcher = (url: string): Promise<any> => fetch(url).then((res) => res.json());

export interface TrendsPageProps {
  initTrends: {
    trends: TrendProps[];
    totalTrends: number;
    page: number;
    pageSize: number;
  }
  initParams: any;
  initQueryParams: any;
}

interface TrendProps {
  id: number;
  type: string;
  threshold: number;
  scope: string;
  teamRole: string;
  half: number;
  trend: {
    matchedFixtures: number;
    totalFixtures: number;
    percentage: number;
    totalAverage: number;
    totalStat: number;
  };
  team: {
    id: number;
    logo: string;
    name: string;
  };
  league: {
    id: number;
    name: string;
    logo: string;
    countryCode: string;
    countryName: string;
    countryFlag: string;
  };
  nextFixture: {
    teamRole: string;
    id: number;
    dateTime: string;
    timezone: string;
    referee?: string;
    venueId?: number;
  };
  opponent: {
    team: {
      id: number;
      name: string;
      logo: string;
    };
    trend: {
      type: string;
      scope: string;
      teamRole: string;
      half: number;
      matchedFixtures: number;
      totalFixtures: number;
      percentage: number;
      totalAverage: number;
      totalStat: number;
    };
  };
  insertionDate: string;
}

const TrendsTable: React.FC<TrendsPageProps> = ({ initTrends, initQueryParams }) => {
  const router = useRouter();
  const { locale } = useParams();
  const { width } = useWindowSize();
  const tTable = useTranslations('table');
  const tFilter = useTranslations('filter');
  const tTrends = useTranslations('trends');

  const [sortBy, setSortBy] = useState<TableKeys>(initQueryParams.sortBy || 'matchedFixtures');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(initQueryParams.sortDirection || 'desc');
  const [currentPage, setCurrentPage] = useState(Number(initQueryParams.page) || 1);
  const [statType, setStatType] = useState<StatType | ''>(initQueryParams.statType || '');
  const [searchParams, setSearchParams] = useState<{ searchType: 'league' | 'team'; searchId: number } | null>(
    initQueryParams.searchType && initQueryParams.searchId
      ? { searchType: initQueryParams.searchType, searchId: Number(initQueryParams.searchId) }
      : null
  );
  const [teamOrLeagueSelected, setTeamOrLeagueSelected] = useState<any>([]);
  const [clearSearch, setClearSearch] = useState(false);

  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const queryString = new URLSearchParams({
    page: currentPage.toString(),
    sortBy,
    sortDirection,
    ...(statType && { statType }),
    ...(searchParams?.searchType && searchParams?.searchId && {
      searchType: searchParams.searchType,
      searchId: searchParams.searchId.toString(),
    }),
  }).toString();

  useEffect(() => {
    router.push(`/${locale}/trends?${queryString}`, { scroll: false });
  }, [queryString]);

  useEffect(() => {
    mutate(`/api/trends?${queryString}`, initTrends, false);
  }, [initTrends]);
  
  const { data, isLoading } = useSWR(`/api/trends?${queryString}`, fetcher,
    {
      fallbackData: initTrends,
      revalidateOnFocus: false
    }
  );

  const trends = data?.trends || [];
  const totalPages = Math.ceil((data?.totalTrends || 1) / (data?.pageSize || 15));

  const handleSearchSelect = (result: any) => {
    setSearchParams({ searchType: result.type, searchId: result.id });
    setTeamOrLeagueSelected(result);
    setCurrentPage(1);
    setClearSearch(true);
  };

  const handleClearFilters = () => {
    setSearchParams(null);
    setSortBy('matchedFixtures');
    setSortDirection('desc');
    setCurrentPage(1);
    setStatType('');
  };

  const handleFilterChange = useCallback((filter: { statType: StatType | '' }) => {
    setStatType(filter.statType);
    setCurrentPage(1);
  }, []);

  const handleSortChange = (sortKey: TableKeys, direction: 'asc' | 'desc') => {
    setSortBy(sortKey);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const clearSearchField = () => {
    setClearSearch(true);
  };

  const headers: Header[] = width > 720 ? [
    { label: <span key="country"></span> },
    { label: <span key="league">{tTable('league')}</span> },
    { label: <span key="teamLogo"></span> },
    { label: <span key="teamName">{tTable('team')}</span> },
    { label: <span key="trend">{tTrends('trends')}</span>, sortKey: 'matchedFixtures' },
    { label: <span key="nextFixture">{tTable('nextFixture')}</span> },
    { label: <span key="nextFixtureDate">{tTable('date')}</span>, sortKey: 'date' }
  ] : [
    { label: <span key="teamName">{tTable('league')}/{tTable('team')}</span> },
    { label: <span key="trend">Trend</span>, sortKey: 'matchedFixtures' },
    { label: <span key="nextFixture">{tTable('nextFixture')}</span>, sortKey: 'date' },
  ];


  const rows = trends?.length > 0 ? trends.map((trend: any) => (
    <React.Fragment key={trend.id}>
      { width > 720 ? 
        <>
          <td className='' title={trend.league.countryName}>
            <Image src={`/icons/flags/${trend.league.countryCode?.toLowerCase()}.svg`} alt={trend.league.countryName} width={20} height={15} />
          </td>
          <td className='league-column'>{trend.league.name}</td>
          <td className=''><Image src={trend.team.logo} alt="Team Logo" width={20} height={20} loading="lazy"  /></td>
          <td className=''>{trend.team.name}</td>
        </> 
        :
        <td className='d-flex flex-column'>
          <div className='d-flex flex-row mb-3'>
            <Image src={`/icons/flags/${trend.league.countryCode?.toLowerCase()}.svg`} alt={trend.league.countryName} width={20} height={15} loading="lazy"  />
            <span>{trend.league.name}</span>
          </div>
          <div className='d-flex flex-row'>
            <Image src={trend.team.logo} alt="Team Logo" width={18} height={18} loading="lazy"  />
            <span>{trend.team.name}</span>
          </div>
        </td>
      }
      <td className='trend-column'><TrendDescription trend={trend} /></td>
      { width > 720 ? 
        <>
          <td className=''>{`${tFilter(trend.nextFixture.teamRole)} vs ${trend.opponent.team.name}`}</td>
          <td className=''>{format(new Date(trend.nextFixture.dateTime), 'MM-dd HH:mm', { locale: es })}</td>
        </>
        :
        <td className='d-flex flex-column'>
          <div className='mb-2'>
            {`${tFilter(trend.nextFixture.teamRole)} vs ${trend.opponent.team.name}`}
          </div>
          <div>
            {format(new Date(trend.nextFixture.dateTime), 'MM-dd HH:mm', { locale: es })}
          </div>
        </td>
      }
    </React.Fragment>
  )) : [<React.Fragment key="noTrendForLeagueNorTeam"><td className="text-center py-5" colSpan={12}>{tTable('noTrends')}</td></React.Fragment>]

  const subRows = trends?.map((trend: any) => {
    const opponentTrend = {
      type: trend.opponent.trend.type,
      threshold: trend.threshold,
      teamRole: trend.opponent.trend.teamRole,
      scope: trend.opponent.trend.scope,
      half: trend.opponent.trend.half ?? 0,
      trend: {
        percentage: trend.opponent.trend.percentage,
        matchedFixtures: trend.opponent.trend.matchedFixtures,
        totalFixtures: trend.opponent.trend.totalFixtures
      }
    }

    const isTrendWithoutAverage = trend.opponent.trend.type === "btts" || trend.opponent.trend.type === "win" 
      || trend.opponent.trend.type === "draw" || trend.opponent.trend.type === "loss"; 

    return (
      <React.Fragment key={`${trend.id}-sub`}>
        <td className='d-flex flex-column'>
          <div className='mb-2'>Counter Trend: </div>
          <div className='d-flex'>
            <Image src={trend.opponent.team.logo} alt="Opponent Logo" width={20} height={20} loading="lazy"  />
            {trend.opponent.team.name}
          </div>
        </td>
        <td className=''><TrendDescription trend={opponentTrend} /></td>
        <td className='d-flex flex-column'>
          <div>
            {!isTrendWithoutAverage ? `Avg: ${trend.opponent.trend.totalAverage}` : ""}
          </div>
          <div>
            {!isTrendWithoutAverage ? `Total: ${trend.opponent.trend.totalStat}` : ""}
          </div>
        </td>
      </React.Fragment>
    );
  })

  const mockedRowsLength = 5
  const mockedRows = Array.from({ length: mockedRowsLength }).flatMap(() => mockedTrends).map(trend => (
    <React.Fragment key={trend.id}>
      { width > 720 ? 
        <>
          <td className='' title={trend.league.countryName}>
            <Image src={`/icons/flags/${trend.league.countryCode?.toLowerCase()}.svg`} alt={trend.league.countryName} width={20} height={15} loading="lazy"  />
          </td>
          <td className='league-column'>{trend.league.name}</td>
          <td className=''><Image src={trend.team.logo} alt="Team Logo" width={20} height={20} loading="lazy"  /></td>
          <td className=''>{trend.team.name}</td>
        </> 
        :
        <td className='d-flex flex-column'>
          <div className='d-flex flex-row mb-3'>
            <Image src={`/icons/flags/${trend.league.countryCode?.toLowerCase()}.svg`} alt={trend.league.countryName} width={20} height={15} />
            <span>{trend.league.name}</span>
          </div>
          <div className='d-flex flex-row'>
            <Image src={trend.team.logo} alt="Team Logo" width={18} height={18} loading="lazy" />
            <span>{trend.team.name}</span>
          </div>
        </td>
      }
      <td className='trend-column'><TrendDescription trend={trend} /></td>
      { width > 720 ? 
        <>
          <td className=''>{`${tFilter(trend.nextFixture.teamRole)} vs ${trend.opponent.team.name}`}</td>
          <td className=''>{format(new Date(trend.nextFixture.dateTime), 'MM-dd HH:mm', { locale: es })}</td>
        </>
        :
        <td className='d-flex flex-column'>
          <div className='mb-2'>
            {`${tFilter(trend.nextFixture.teamRole)} vs ${trend.opponent.team.name}`}
          </div>
          <div>
            {format(new Date(trend.nextFixture.dateTime), 'MM-dd HH:mm', { locale: es })}
          </div>
        </td>
      }
    </React.Fragment>
  ))

  return (
    <>
      <div className="glass-segment p-2 p-sm-3 d-flex flex-wrap position-relative" style={{ zIndex: 5000 }}>
        <div className='col-12 col-md-8 px-1'>
          <SearchDropdown
            onSelect={handleSearchSelect}
            clearSearch={clearSearch}
            options={[]}
            placeholder={teamOrLeagueSelected?.name}
            sourceOfData="dynamic"
            label={tFilter('leagueOrTeam')}
          />
        </div>
        <div className='col-6 col-md-2 px-1'>
          <StatTypeFilter onFilterChange={handleFilterChange} statType={statType} />
        </div>
        <div className='col-6 col-md-2 px-1'>
          <label>&nbsp;</label>
          <button 
            className="btn form-control btn-primary-outline"
            onClick={() => { handleClearFilters(); clearSearchField(); }}
          >
            {tFilter('clear')}
          </button>
        </div>
      </div>
      <Table
        headers={headers}
        rows={rows}
        mockedRows={mockedRows}
        subRows={subRows}
        totalPages={totalPages}
        currentPage={currentPage}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onSortChange={(sortKey, direction) => handleSortChange(sortKey, direction)}
        onPageChange={(page) => setCurrentPage(page)}
        loading={hasMounted && isLoading}
      />
      <div className="d-block d-md-none floating-div glass-segment">
        <a className='nav-link' href="#trendsFilters">Filters</a>
      </div>
    </>
  )
}

export default TrendsTable;